import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Wallet, AlertCircle, TrendingDown, Plus, RefreshCw, Check, X,
  Truck, Calendar, Filter, FileDown, Repeat, Tag,
} from 'lucide-react';
import { buildPayablesReportHtml } from '@/lib/payablesReport';

const STATUS_LABELS = {
  pendente: { label: 'Pendente', color: 'bg-blue-100 text-blue-800' },
  parcial: { label: 'Parcial', color: 'bg-amber-100 text-amber-800' },
  vencido: { label: 'Vencido', color: 'bg-rose-100 text-rose-800' },
  pago: { label: 'Pago', color: 'bg-emerald-100 text-emerald-800' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600' },
};

const CATEGORIAS = [
  'Aluguel', 'Energia', 'Água', 'Internet/Telefone', 'Fornecedores',
  'Salários', 'Impostos', 'Manutenção', 'Marketing', 'Transporte', 'Outros',
];

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('pt-BR'); } catch { return iso; }
};

const ContasPagarPage = () => {
  const [itens, setItens] = useState([]);
  const [resumo, setResumo] = useState({ pendente: {}, vencido: {}, pago_mes: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [reportFilters, setReportFilters] = useState({ de: '', ate: '', fornecedor_id: '' });
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const [formCreate, setFormCreate] = useState({
    fornecedor_id: '', categoria: 'Outros', valor_original: '',
    data_vencimento: '', descricao: '', observacoes: '', recorrente: false,
  });
  const [formPay, setFormPay] = useState({ valor: '', forma_pagamento: 'dinheiro', observacao: '' });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, resumoResp] = await Promise.all([
        api.get(`/payables${statusFilter ? `?status=${statusFilter}` : ''}`),
        api.get('/payables/resumo'),
      ]);
      setItens(listResp.data.itens || []);
      setResumo(resumoResp.data || {});
    } catch (e) {
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/fornecedores')
      .then((r) => setFornecedores(r.data.itens || []))
      .catch(() => setFornecedores([]));
  }, []);

  const criar = async () => {
    if (!formCreate.valor_original || !formCreate.data_vencimento) {
      toast.error('Valor e data de vencimento são obrigatórios');
      return;
    }
    try {
      const forn = fornecedores.find((f) => f.id === formCreate.fornecedor_id);
      await api.post('/payables', {
        fornecedor_id: formCreate.fornecedor_id || null,
        fornecedor_nome: forn?.nome,
        categoria: formCreate.categoria,
        valor_original: Number(formCreate.valor_original),
        data_vencimento: formCreate.data_vencimento,
        descricao: formCreate.descricao,
        observacoes: formCreate.observacoes,
        recorrente: formCreate.recorrente,
      });
      toast.success(formCreate.recorrente ? 'Conta recorrente criada' : 'Conta a pagar criada');
      setShowCreate(false);
      setFormCreate({ fornecedor_id: '', categoria: 'Outros', valor_original: '', data_vencimento: '', descricao: '', observacoes: '', recorrente: false });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar');
    }
  };

  const pagar = async () => {
    if (!formPay.valor || Number(formPay.valor) <= 0) { toast.error('Informe o valor do pagamento'); return; }
    try {
      await api.post(`/payables/${showPay.id}/pagar`, {
        valor: Number(formPay.valor),
        forma_pagamento: formPay.forma_pagamento,
        observacao: formPay.observacao,
      });
      toast.success('Pagamento registrado');
      setShowPay(null);
      setFormPay({ valor: '', forma_pagamento: 'dinheiro', observacao: '' });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar esta conta a pagar?')) return;
    try {
      await api.delete(`/payables/${id}`);
      toast.success('Conta cancelada');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const gerarRelatorioPdf = async () => {
    setGerandoPdf(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (reportFilters.de) params.set('de', reportFilters.de);
      if (reportFilters.ate) params.set('ate', reportFilters.ate);
      if (reportFilters.fornecedor_id) params.set('fornecedor_id', reportFilters.fornecedor_id);
      const r = await api.get(`/payables/relatorio?${params.toString()}`);
      const w = window.open('', '_blank', 'width=1000,height=900');
      if (!w) { toast.error('Permita pop-ups para gerar o PDF'); return; }
      w.document.write(buildPayablesReportHtml(r.data));
      w.document.close();
      toast.success('Relatório gerado — use "Salvar em PDF" na janela aberta');
    } catch (e) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="contas-pagar-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Contas a Pagar</h1>
          <p className="text-slate-600">Despesas e fornecedores, com baixa parcial/total e recorrência</p>
        </div>
        <div className="flex gap-2">
          <Link to="/fornecedores">
            <Button variant="outline" data-testid="btn-fornecedores">
              <Truck className="w-4 h-4 mr-2" /> Fornecedores
            </Button>
          </Link>
          <Button variant="outline" onClick={carregar} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-create-payable">
            <Plus className="w-4 h-4 mr-2" /> Nova conta
          </Button>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card data-testid="card-pendente">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-blue-500" /> A pagar (pendente)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{formatCurrency(resumo.pendente?.total || 0)}</div>
            <p className="text-xs text-slate-500 mt-1">{resumo.pendente?.count || 0} conta(s) em aberto</p>
          </CardContent>
        </Card>
        <Card data-testid="card-vencido">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500" /> Vencidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-rose-600">{formatCurrency(resumo.vencido?.total || 0)}</div>
            <p className="text-xs text-slate-500 mt-1">{resumo.vencido?.count || 0} conta(s) atrasada(s)</p>
          </CardContent>
        </Card>
        <Card data-testid="card-pago-mes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-emerald-500" /> Pago este mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{formatCurrency(resumo.pago_mes?.total || 0)}</div>
            <p className="text-xs text-slate-500 mt-1">{resumo.pago_mes?.count || 0} pagamento(s)</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6 flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <Label>Status:</Label>
          </div>
          <Select value={statusFilter || 'todos'} onValueChange={(v) => setStatusFilter(v === 'todos' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="parcial">Parcial</SelectItem>
              <SelectItem value="vencido">Vencido</SelectItem>
              <SelectItem value="pago">Pago</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <div>
            <Label className="text-xs text-slate-500">Vencimento de</Label>
            <Input type="date" value={reportFilters.de} onChange={(e) => setReportFilters({ ...reportFilters, de: e.target.value })} className="w-40" data-testid="report-date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">até</Label>
            <Input type="date" value={reportFilters.ate} onChange={(e) => setReportFilters({ ...reportFilters, ate: e.target.value })} className="w-40" data-testid="report-date-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Fornecedor</Label>
            <Select value={reportFilters.fornecedor_id || 'todos'} onValueChange={(v) => setReportFilters({ ...reportFilters, fornecedor_id: v === 'todos' ? '' : v })}>
              <SelectTrigger className="w-52" data-testid="report-fornecedor"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os fornecedores</SelectItem>
                {fornecedores.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={gerarRelatorioPdf} disabled={gerandoPdf} className="ml-auto bg-slate-900 hover:bg-slate-800" data-testid="btn-report-pdf">
            {gerandoPdf ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Relatório PDF
          </Button>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tabela-payables">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium">Fornecedor</th>
                <th className="text-left p-3 font-medium">Categoria / Descrição</th>
                <th className="text-right p-3 font-medium">Valor</th>
                <th className="text-right p-3 font-medium">Pago</th>
                <th className="text-right p-3 font-medium">Restante</th>
                <th className="text-center p-3 font-medium">Vencimento</th>
                <th className="text-center p-3 font-medium">Status</th>
                <th className="text-center p-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500">Carregando...</td></tr>
              ) : itens.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500">
                  Nenhuma conta a pagar encontrada. Clique em &quot;Nova conta&quot; para criar.
                </td></tr>
              ) : itens.map((r) => {
                const st = STATUS_LABELS[r.status] || STATUS_LABELS.pendente;
                return (
                  <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`row-payable-${r.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-400" />
                        {r.fornecedor_nome}
                        {r.recorrente && <Repeat className="w-3 h-3 text-indigo-500" title="Conta recorrente" />}
                      </div>
                    </td>
                    <td className="p-3 text-slate-600">
                      <span className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-2 py-0.5">
                        <Tag className="w-3 h-3" /> {r.categoria || 'Outros'}
                      </span>
                      {r.descricao ? <div className="text-xs text-slate-500 mt-0.5">{r.descricao}</div> : null}
                    </td>
                    <td className="p-3 text-right font-medium">{formatCurrency(r.valor_original)}</td>
                    <td className="p-3 text-right text-emerald-600">{formatCurrency(r.valor_pago)}</td>
                    <td className="p-3 text-right text-blue-600 font-medium">{formatCurrency(r.valor_restante)}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-slate-400" /> {fmtDate(r.data_vencimento)}
                      </div>
                    </td>
                    <td className="p-3 text-center"><Badge className={st.color}>{st.label}</Badge></td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        {r.status !== 'pago' && r.status !== 'cancelado' && (
                          <Button size="sm" variant="outline"
                            onClick={() => { setShowPay(r); setFormPay({ valor: String(r.valor_restante), forma_pagamento: 'dinheiro', observacao: '' }); }}
                            data-testid={`btn-pay-${r.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Pagar
                          </Button>
                        )}
                        {r.status !== 'pago' && r.status !== 'cancelado' && (
                          <Button size="sm" variant="ghost" onClick={() => cancelar(r.id)} data-testid={`btn-cancel-${r.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="modal-create-payable">
          <DialogHeader><DialogTitle>Nova conta a pagar</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fornecedor</Label>
                <Select value={formCreate.fornecedor_id || 'nenhum'} onValueChange={(v) => setFormCreate({ ...formCreate, fornecedor_id: v === 'nenhum' ? '' : v })}>
                  <SelectTrigger data-testid="select-fornecedor"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhum">Sem fornecedor</SelectItem>
                    {fornecedores.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={formCreate.categoria} onValueChange={(v) => setFormCreate({ ...formCreate, categoria: v })}>
                  <SelectTrigger data-testid="select-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor (R$) *</Label>
                <Input type="number" step="0.01" value={formCreate.valor_original}
                  onChange={(e) => setFormCreate({ ...formCreate, valor_original: e.target.value })} data-testid="input-valor" />
              </div>
              <div>
                <Label>Vencimento *</Label>
                <Input type="date" value={formCreate.data_vencimento}
                  onChange={(e) => setFormCreate({ ...formCreate, data_vencimento: e.target.value })} data-testid="input-vencimento" />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={formCreate.descricao} onChange={(e) => setFormCreate({ ...formCreate, descricao: e.target.value })} placeholder="Ex: Aluguel loja centro" />
            </div>
            <div>
              <Label>Observações</Label>
              <Input value={formCreate.observacoes} onChange={(e) => setFormCreate({ ...formCreate, observacoes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div>
                <Label className="text-base flex items-center gap-2"><Repeat className="w-4 h-4 text-indigo-600" /> Conta recorrente (mensal)</Label>
                <p className="text-xs text-slate-500 mt-1">Gera automaticamente todo mês, na mesma data de vencimento.</p>
              </div>
              <Switch checked={formCreate.recorrente} onCheckedChange={(v) => setFormCreate({ ...formCreate, recorrente: v })} data-testid="switch-recorrente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={criar} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirm-create">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Pagar */}
      <Dialog open={!!showPay} onOpenChange={(open) => !open && setShowPay(null)}>
        <DialogContent data-testid="modal-pay-payable">
          <DialogHeader><DialogTitle>Registrar pagamento</DialogTitle></DialogHeader>
          {showPay && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded">
                <p className="text-sm text-slate-600">Fornecedor: <strong>{showPay.fornecedor_nome}</strong></p>
                <p className="text-sm text-slate-600">Restante: <strong className="text-blue-600">{formatCurrency(showPay.valor_restante)}</strong></p>
              </div>
              <div>
                <Label>Valor pago *</Label>
                <Input type="number" step="0.01" value={formPay.valor}
                  onChange={(e) => setFormPay({ ...formPay, valor: e.target.value })} data-testid="input-pay-valor" />
                <p className="text-xs text-slate-500 mt-1">Aceita pagamento parcial. Valor máximo: {formatCurrency(showPay.valor_restante)}</p>
              </div>
              <div>
                <Label>Forma de pagamento</Label>
                <Select value={formPay.forma_pagamento} onValueChange={(v) => setFormPay({ ...formPay, forma_pagamento: v })}>
                  <SelectTrigger data-testid="select-forma-pagamento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de crédito</SelectItem>
                    <SelectItem value="cartao_debito">Cartão de débito</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observação</Label>
                <Input value={formPay.observacao} onChange={(e) => setFormPay({ ...formPay, observacao: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(null)}>Cancelar</Button>
            <Button onClick={pagar} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirm-pay">Registrar pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasPagarPage;
